import { useState, FormEvent, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const ISSUE_CATEGORIES = [
  "Billing & Payments",
  "Subscription Management",
  "Technical Issues",
  "Account Access",
  "General Inquiry",
  "Other"
];

export default function ContactSupportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    message: ""
  });

  const descLen = description.length;
  const descValid = descLen >= 20 && descLen <= 500;

    const topRef = useRef<HTMLDivElement>(null);
  
    useEffect(() => {
      if (submitted) {
        setTimeout(() => {
          topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }, [submitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let text = "";
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: formData.category,
      to_email: "support@regent-digital.com",
      subject: "Support Request"
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={topRef} className="min-h-screen flex flex-col bg-white font-sans">
      <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset;
      }
    `}</style>
      <Navbar />
      {/* Page header */}
      <div className="bg-[#F5C518] pt-28 pb-12 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-black tracking-widest uppercase text-black/50 hover:text-black transition-colors cursor-pointer mb-6"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
              <i className="ri-customer-service-2-line text-[#F5C518] text-xl"></i>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-black leading-none">
              Contact Support
            </h1>
          </div>
          <div className="w-16 h-1 bg-black mt-4"></div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 sm:px-10 py-16">
        <div className="max-w-2xl">
          <div className="mb-10">
            <p className="text-black/70 text-base leading-relaxed">
              Connect directly with our support specialists for immediate
              assistance with your account and service inquiries. We typically
              respond within 24 hours.
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#F5C518] p-8 sm:p-10 text-center">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-[#F5C518] text-xl"></i>
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-black mb-2">
                Message Sent
              </h3>
              <p className="text-black/60 text-sm">
                Thank you for reaching out. Our support team will get back to
                you within 24 hours.
              </p>
              <Link
                to="/"
                className="inline-block mt-6 px-6 py-3 bg-black text-[#F5C518] text-xs font-black tracking-widest uppercase hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Help Center
              </Link>
            </div>
          ) : (
            <form
              id="contact-support-form"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Name + Email */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="Enter your full name"
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="your.email@example.com"
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Issue Category */}
              <div>
                <label className="block text-sm font-black text-black mb-4">
                  Issue Category
                </label>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {ISSUE_CATEGORIES.map(cat => (
                    <label
                      key={cat}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="relative w-5 h-5 flex-shrink-0">
                        <input
                          type="radio"
                          name="issue_category"
                          value={cat}
                          required
                          checked={category === cat}
                          onChange={() => {
                            setCategory(cat);
                            setFormData({ ...formData, category: cat });
                          }}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 transition-colors ${category === cat ? "border-black" : "border-black/30 group-hover:border-black/60"}`}
                        >
                          {category === cat && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-2.5 h-2.5 rounded-full bg-black"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-sm font-semibold transition-colors ${category === cat ? "text-black" : "text-black/60 group-hover:text-black"}`}
                      >
                        {cat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Issue Description */}
              <div>
                <label className="block text-sm font-black text-black mb-2">
                  Issue Description
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={7}
                  value={description}
                  onChange={e => {
                    setFormData({ ...formData, message: e.target.value });
                    setDescription(e.target.value);
                  }}
                  className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none bg-white rounded-lg"
                  placeholder="Please provide detailed information about your issue. Include any error messages, steps you've taken, and what you expected to happen."
                />
                <div className="flex items-center justify-between mt-1">
                  <span
                    className={`text-xs ${descLen > 0 && descLen < 20 ? "text-red-500" : "text-black/40"}`}
                  >
                    Minimum 20 characters required
                  </span>
                  <span
                    className={`text-xs font-semibold ${descLen > 480 ? "text-red-500" : "text-black/40"}`}
                  >
                    {descLen} / 500
                  </span>
                </div>
              </div>

              {errorSubmit && (
                <p className="text-red-500 text-sm">
                  Something went wrong. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !descValid}
                className="w-full sm:w-auto px-8 py-3 bg-black text-[#F5C518] text-xs font-black tracking-widest uppercase hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 rounded-lg"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
